import { createAction, createReducer, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'common/services/message';

import api from 'common/api';
import { PaginationState } from 'common/components/Pagination';
import { AppState } from 'common/models/AppState';
import { Token, TokenState } from 'common/models/Token';
import i18n from 'common/services/i18n';
import { selectForm, selectFormTransaction } from 'features/form/ducks';

import { startListening } from '../../../../../../root/store/listener-middleware';

export const selectVerificationLog = (state: AppState) => selectForm(state).documents.verificationLog;

export const selectVerificationLogItems = (state: AppState) => selectVerificationLog(state).items;

export const selectIsVerificationLogVisible = (state: AppState) => selectVerificationLog(state).isVisible;

export const selectVerificationLogPagination = (state: AppState) => selectVerificationLog(state).pagination;

export const loadVerificationLogAction = createAction('@@Documents/VerificationLog/LOAD');

export const setVerificationLogVisibleAction = createAction<boolean>('@@Documents/VerificationLog/SET_VISIBILITY');

export const setVerificationLogItemsAction = createAction<Token[]>('@@Documents/VerificationLog/SET_ITEMS');

export const setVerificationLogItemCountAction = createAction<number>('@@Documents/VerificationLog/SET_ITEM_COUNT');

export const setVerificationLogPageNumberAction = createAction<number>('@@Documents/VerificationLog/SET_PAGE_NUMBER');

export const setVerificationLogPageSizeAction = createAction<number>('@@Documents/VerificationLog/SET_PAGE_SIZE');

export interface VerificationLogState {
  items: Token[];
  isVisible: boolean;
  pagination: PaginationState;
}

export const initialVerificationLogState: VerificationLogState = {
  items: [],
  isVisible: false,
  pagination: { currentPage: 1, pageSize: 5, total: 0 },
};

export default createReducer(initialVerificationLogState, (builder) => {
  builder
    .addCase(setVerificationLogVisibleAction, (state, { payload }) => {
      state.isVisible = payload;
    })
    .addCase(setVerificationLogItemsAction, (state, { payload }) => {
      state.items = payload;
    })
    .addCase(setVerificationLogItemCountAction, (state, { payload }) => {
      state.pagination.total = payload;
    })
    .addCase(setVerificationLogPageNumberAction, (state, { payload }) => {
      state.pagination.currentPage = payload;
    })
    .addCase(setVerificationLogPageSizeAction, (state, { payload }) => {
      state.pagination.currentPage = 1;
      state.pagination.pageSize = payload;
    });
});

// Listeners
startListening({
  actionCreator: loadVerificationLogAction,
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const state = listenerApi.getState();
    const transaction = selectFormTransaction(state);
    const pagination = selectVerificationLogPagination(state);

    const { ok, data } = await api.token.find({
      status: TokenState.USED,
      $skip: pagination.pageSize * (pagination.currentPage - 1),
      $limit: pagination.pageSize,
      'meta.transactionId': transaction!._id,
    });

    if (ok && data) {
      listenerApi.dispatch(setVerificationLogItemsAction(data.data));
      listenerApi.dispatch(setVerificationLogItemCountAction(data.total));
    } else {
      const t = await i18n;
      message.error(t('formsManagement.failedLoad'));
    }
  },
});

startListening({
  matcher: isAnyOf(setVerificationLogPageNumberAction, setVerificationLogPageSizeAction),
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    listenerApi.dispatch(loadVerificationLogAction());
  },
});

startListening({
  actionCreator: setVerificationLogVisibleAction,
  effect: async (action, listenerApi) => {
    if (action.payload) {
      listenerApi.dispatch(loadVerificationLogAction());
    }
  },
});
